import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, UserPlusIcon } from '@heroicons/react/24/outline';

interface Profesor {
  id: number;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  rol: 'titular' | 'jtp' | 'ayudante';
  activo: boolean;
  createdAt: string;
  updatedAt: string;
  tieneUsuario?: boolean;
}

const ProfesoresPage: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingProfesor, setEditingProfesor] = useState<Profesor | null>(null);
  const [showDetails, setShowDetails] = useState<Profesor | null>(null);
  const [selectedProfesor, setSelectedProfesor] = useState<Profesor | null>(null);
  const queryClient = useQueryClient();

  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    rol: 'jtp' as 'titular' | 'jtp' | 'ayudante',
    activo: true
  });

  // User form state
  const [userFormData, setUserFormData] = useState({
    email: '',
    nombre: '',
    apellido: '',
    rol: 'profesor' as 'admin' | 'profesor' | 'jtp' | 'ayudante'
  });

  // Fetch profesores
  const { data: profesores, isLoading, error } = useQuery({
    queryKey: ['profesores'],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/profesores`);
      return response.data;
    }
  });

  // Create profesor mutation
  const createProfesorMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/profesores`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profesores'] });
      setShowModal(false);
      resetForm();
    }
  });

  // Update profesor mutation
  const updateProfesorMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/profesores/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profesores'] });
      setShowModal(false);
      setEditingProfesor(null);
      resetForm();
    }
  });

  // Delete profesor mutation
  const deleteProfesorMutation = useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`${process.env.REACT_APP_API_URL}/profesores/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profesores'] });
    }
  });

  // Create user for profesor mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/usuarios`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      setShowUserModal(false);
      setSelectedProfesor(null);
      resetUserForm();
    }
  });

  // Regenerate code mutation
  const regenerateCodeMutation = useMutation({
    mutationFn: async (profesorId: number) => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/profesores/${profesorId}/regenerar-codigo`);
      return response.data;
    },
    onSuccess: (data) => {
      alert(data.message);
    }
  });

  // Check if profesor has user
  const checkProfesorUser = async (profesorId: number): Promise<boolean> => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/profesores/${profesorId}/tiene-usuario`);
      return response.data.tieneUsuario;
    } catch (error) {
      console.error('Error verificando usuario del profesor:', error);
      return false;
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      rol: 'jtp',
      activo: true
    });
  };

  const resetUserForm = () => {
    setUserFormData({
      email: '',
      nombre: '',
      apellido: '',
      rol: 'profesor' as 'admin' | 'profesor' | 'jtp' | 'ayudante'
    });
  };

  const handleEdit = (profesor: Profesor) => {
    setEditingProfesor(profesor);
    setFormData({
      nombre: profesor.nombre,
      apellido: profesor.apellido,
      email: profesor.email,
      telefono: profesor.telefono,
      rol: profesor.rol,
      activo: profesor.activo
    });
    setShowModal(true);
  };

  const handleCreateUser = (profesor: Profesor) => {
    setSelectedProfesor(profesor);
    
    // Mapear rol de profesor a rol de usuario
    let rolUsuario: 'admin' | 'profesor' | 'jtp' | 'ayudante';
    switch (profesor.rol) {
      case 'titular':
        rolUsuario = 'profesor';
        break;
      case 'jtp':
        rolUsuario = 'jtp';
        break;
      case 'ayudante':
        rolUsuario = 'ayudante';
        break;
      default:
        rolUsuario = 'ayudante';
    }
    
    setUserFormData({
      email: profesor.email,
      nombre: profesor.nombre,
      apellido: profesor.apellido,
      rol: rolUsuario
    });
    setShowUserModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingProfesor) {
      updateProfesorMutation.mutate({ id: editingProfesor.id, data: formData });
    } else {
      createProfesorMutation.mutate(formData);
    }
  };

  const handleUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createUserMutation.mutate(userFormData);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este profesor?')) {
      deleteProfesorMutation.mutate(id);
    }
  };

  const getRolLabel = (rol: string) => {
    switch (rol) {
      case 'titular': return 'Profesor Titular';
      case 'jtp': return 'JTP';
      case 'ayudante': return 'Ayudante';
      default: return rol;
    }
  };

  if (isLoading) return <div className="text-center py-8">Cargando profesores...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error al cargar los profesores</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Profesores</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Nuevo Profesor
        </button>
      </div>

      {/* Lista de profesores */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Profesores ({profesores?.length || 0})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profesor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(profesores) && profesores.map((profesor: Profesor) => (
                <tr key={profesor.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {profesor.nombre} {profesor.apellido}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{profesor.email}</div>
                    <div className="text-sm text-gray-500">{profesor.telefono}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      profesor.rol === 'titular' ? 'bg-purple-100 text-purple-800' :
                      profesor.rol === 'jtp' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {getRolLabel(profesor.rol)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      profesor.activo 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {profesor.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowDetails(profesor)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(profesor)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {!profesor.tieneUsuario && (
                        <button
                          onClick={() => handleCreateUser(profesor)}
                          className="text-green-600 hover:text-green-900"
                          title="Crear usuario"
                        >
                          <UserPlusIcon className="h-4 w-4" />
                        </button>
                      )}
                      {profesor.tieneUsuario && (
                        <span
                          className="text-green-600"
                          title="Usuario ya creado"
                        >
                          ✅
                        </span>
                      )}
                      <button
                        onClick={() => handleDelete(profesor.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para crear/editar profesor */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingProfesor ? 'Editar Profesor' : 'Nuevo Profesor'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Apellido</label>
                    <input
                      type="text"
                      value={formData.apellido}
                      onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rol</label>
                    <select
                      value={formData.rol}
                      onChange={(e) => setFormData({...formData, rol: e.target.value as 'titular' | 'jtp' | 'ayudante'})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="titular">Profesor Titular</option>
                      <option value="jtp">JTP</option>
                      <option value="ayudante">Ayudante</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Profesor Activo</label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingProfesor(null);
                      resetForm();
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createProfesorMutation.isPending || updateProfesorMutation.isPending}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createProfesorMutation.isPending || updateProfesorMutation.isPending ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear usuario */}
      {showUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Crear Usuario para {selectedProfesor?.nombre} {selectedProfesor?.apellido}
              </h3>
              <form onSubmit={handleUserSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={userFormData.nombre}
                      onChange={(e) => setUserFormData({...userFormData, nombre: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Apellido</label>
                    <input
                      type="text"
                      value={userFormData.apellido}
                      onChange={(e) => setUserFormData({...userFormData, apellido: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={userFormData.email}
                      onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rol de Usuario</label>
                    <select
                      value={userFormData.rol}
                      onChange={(e) => setUserFormData({...userFormData, rol: e.target.value as 'admin' | 'profesor' | 'jtp' | 'ayudante'})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="profesor">Profesor</option>
                      <option value="jtp">JTP</option>
                      <option value="ayudante">Ayudante</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm text-blue-700">
                    Se enviará un código de acceso al email proporcionado para que el usuario pueda iniciar sesión.
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserModal(false);
                      setSelectedProfesor(null);
                      resetUserForm();
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createUserMutation.isPending}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {createUserMutation.isPending ? 'Creando...' : 'Crear Usuario'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para detalles del profesor */}
      {showDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Detalles del Profesor</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">
                    {showDetails.nombre} {showDetails.apellido}
                  </h4>
                  <p className="text-sm text-gray-600">{showDetails.email}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Teléfono: {showDetails.telefono}</p>
                    <p className="text-sm font-medium text-gray-900">Rol: {getRolLabel(showDetails.rol)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Estado: {showDetails.activo ? 'Activo' : 'Inactivo'}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      Registrado: {new Date(showDetails.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowDetails(null)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfesoresPage;
